import React from 'react';
import ProgramImportLearningContentModal from './ProgramImportLearningContentModal';
import {
  ProgramProgressEmptyPreviewShell,
  type ProgramProgressEmptyTableMode,
} from '../common/ProgressEmptyActualTables';
import type { AdminLearningContentRow } from '../../types/domain';

export type ProgramProgressEmptyStateImportProps = {
  workspaceId: string;
  gradeId: string;
  defaultGradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  termNumber?: 1 | 2 | null;
  preferredTemplateName?: string;
  modalOpen: boolean;
  onCloseModal: () => void;
  onOpenImport: () => void;
  applying: boolean;
  onApply: (rows: AdminLearningContentRow[]) => Promise<void>;
  importBlocked: boolean;
  /** 편제 동기화 미러 화면에서만 true — 가져오기 비활성 */
  syncMirrorView?: boolean;
};

export type ProgramProgressEmptyStateTableCardProps = {
  mode: ProgramProgressEmptyTableMode;
  academicYear: number;
  /** 고정 프로그램은 학년도만 있는 단일 컨트롤 행 */
  toolbarVariant: 'track' | 'fixed' | 'project';
  importProps?: ProgramProgressEmptyStateImportProps;
  postSaveRefreshPending?: boolean;
};

/**
 * 데이터가 없을 때도 편집 화면과 동일한 카드·학년도(·학습내용 가져오기) 블록을 두고, 아래만 빈 표 미리보기.
 * 미리보기에서는 카드 상단 컨트롤·내부 툴바·표가 모두 비활성입니다.
 */
export const ProgramProgressEmptyStateTableCard: React.FC<ProgramProgressEmptyStateTableCardProps> = ({
  mode,
  academicYear,
  toolbarVariant,
  importProps,
  postSaveRefreshPending = false,
}) => {
  const isSplitToolbar = toolbarVariant === 'track' || toolbarVariant === 'project';

  return (
    <article
      className={
        'cp-card cp-weekly-class-hours-card cp-program-progress-page-table-card' +
        (postSaveRefreshPending ? ' is-refreshing' : '')
      }
    >
      {isSplitToolbar ? (
        <div className="cp-weekly-class-hours-controls cp-program-progress-table-card-toolbar">
          <div className="cp-program-progress-table-card-toolbar-left">
            <label>
              학년도
              <span
                className="cp-academic-year-readonly"
                aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}
              >
                {academicYear}학년도
              </span>
            </label>
          </div>
          {importProps ? (
            <button
              type="button"
              className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
              disabled
              tabIndex={-1}
              title="미리보기에서는 사용할 수 없습니다."
            >
              학습내용 가져오기
            </button>
          ) : null}
        </div>
      ) : (
        <div className="cp-weekly-class-hours-controls">
          <label>
            학년도
            <span
              className="cp-academic-year-readonly"
              aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}
            >
              {academicYear}학년도
            </span>
          </label>
        </div>
      )}
      {importProps ? (
        <ProgramImportLearningContentModal
          open={importProps.modalOpen}
          onClose={importProps.onCloseModal}
          workspaceId={importProps.workspaceId}
          gradeId={importProps.gradeId}
          defaultGradeNumber={importProps.defaultGradeNumber}
          termNumber={importProps.termNumber}
          preferredTemplateName={importProps.preferredTemplateName}
          applying={importProps.applying}
          onApply={importProps.onApply}
        />
      ) : null}
      <ProgramProgressEmptyPreviewShell mode={mode} embedInProgramTableCard />
    </article>
  );
};
